<script lang="ts">
	import { onMount } from 'svelte';
	import { Table, TableHead, TableHeadCell, TableBody, TableBodyRow, TableBodyCell, Badge, Button, Input, Spinner, Toggle, Modal, Label } from 'flowbite-svelte';
	import { Icon } from 'flowbite-svelte-icons';
	import PageHeader from '../components/PageHeader.svelte';
	import { listUsers, updateUser } from '../AppNetwork';
	import type { User } from '../AppTypes';

	let users: User[] = [];
	let total = 0;
	let loading = true;
	let error = '';
	let search = '';
	let page = 0;
	const limit = 20;

	let showEditModal: boolean = false;
	let editingUser: User | null = null;
	let formIsAdmin: boolean = false;
	let isSaving: boolean = false;

	async function loadUsers() {
		loading = true;
		error = '';
		try {
			const result = await listUsers({ limit, offset: page * limit, search });
			users = result.items;
			total = result.total;
		} catch (e) {
			error = e instanceof Error ? e.message : 'Failed to load users';
		} finally {
			loading = false;
		}
	}

	function openEditModal(user: User) {
		editingUser = user;
		formIsAdmin = user.isAdmin;
		showEditModal = true;
	}

	async function handleEdit() {
		if (!editingUser) return;
		isSaving = true;
		try {
			await updateUser(editingUser.id, { isAdmin: formIsAdmin });
			showEditModal = false;
			editingUser = null;
			await loadUsers();
		} catch (e) {
			error = e instanceof Error ? e.message : 'Failed to update user';
		}
		isSaving = false;
	}

	function handleSearch() {
		page = 0;
		loadUsers();
	}

	function formatDate(dateString: string): string {
		return new Date(dateString).toLocaleDateString(undefined, {
			year: 'numeric',
			month: 'short',
			day: 'numeric',
			hour: '2-digit',
			minute: '2-digit'
		});
	}

	onMount(loadUsers);
</script>

<PageHeader 
	title="Users" 
	description="Manage user accounts and permissions"
/>

<!-- Search -->
<div class="mb-6 flex gap-4">
	<Input
		bind:value={search}
		placeholder="Search by name or email..."
		class="max-w-md"
		on:keypress={(e) => e.key === 'Enter' && handleSearch()}
	>
		<i slot="left" class="fa-solid fa-search text-gray-400"></i>
	</Input>
	<Button on:click={handleSearch}>Search</Button>
</div>

{#if loading}
	<div class="flex justify-center py-12">
		<Spinner size="8" />
	</div>
{:else if error}
	<div class="text-center py-12">
		<i class="fa-solid fa-exclamation-triangle text-4xl text-red-500 mb-4"></i>
		<p class="text-gray-500 dark:text-gray-400">{error}</p>
		<Button class="mt-4" on:click={loadUsers}>Retry</Button>
	</div>
{:else if users.length === 0}
	<div class="text-center py-12">
		<i class="fa-solid fa-users text-4xl text-gray-300 dark:text-gray-600 mb-4"></i>
		<p class="text-gray-500 dark:text-gray-400">No users found</p>
	</div>
{:else}
	<Table striped divClass="relative overflow-hidden rounded-lg">
		<TableHead class="bg-gray-50 dark:bg-gray-700">
			<TableHeadCell>User</TableHeadCell>
			<TableHeadCell>Email</TableHeadCell>
			<TableHeadCell>Role</TableHeadCell>
			<TableHeadCell>Joined</TableHeadCell>
			<TableHeadCell>Last Login</TableHeadCell>
			<TableHeadCell>Actions</TableHeadCell>
		</TableHead>
		<TableBody>
			{#each users as user}
				<TableBodyRow>
					<TableBodyCell class="font-medium text-gray-900 dark:text-white">
						{user.displayName || 'Unknown'}
					</TableBodyCell>
					<TableBodyCell>{user.email}</TableBodyCell>
					<TableBodyCell>
					{#if user.isAdmin}
							<Badge color="green">Admin</Badge>
						{:else}
							<Badge color="dark">User</Badge>
						{/if}
					</TableBodyCell>
					<TableBodyCell class="text-gray-500 dark:text-gray-400">
						{formatDate(user.creationDate)}
					</TableBodyCell>
					<TableBodyCell class="text-gray-500 dark:text-gray-400">
						{user.lastLogin ? formatDate(user.lastLogin) : 'Never'}
					</TableBodyCell>
					<TableBodyCell>
						<Button size="xs" color="alternative" on:click={() => openEditModal(user)}>
							<Icon name="edit-outline" class="w-3 h-3 mr-1" />Edit
						</Button>
					</TableBodyCell>
				</TableBodyRow>
			{/each}
			{#if users.length === 0}
				<TableBodyRow>
					<TableBodyCell colspan={6} class="text-center text-gray-500">
						No users found
					</TableBodyCell>
				</TableBodyRow>
			{/if}
		</TableBody>
	</Table>

	<!-- Pagination -->
	{#if total > limit}
		<div class="flex justify-between items-center mt-4">
			<p class="text-sm text-gray-500 dark:text-gray-400">
				Showing {page * limit + 1} to {Math.min((page + 1) * limit, total)} of {total} users
			</p>
			<div class="flex gap-2">
				<Button
					disabled={page === 0}
					on:click={() => { page--; loadUsers(); }}
					size="sm"
				>
					<i class="fa-solid fa-chevron-left mr-1"></i>
					Previous
				</Button>
				<Button
					disabled={(page + 1) * limit >= total}
					on:click={() => { page++; loadUsers(); }}
					size="sm"
				>
					Next
					<i class="fa-solid fa-chevron-right ml-1"></i>
				</Button>
			</div>
		</div>
	{/if}
{/if}

<!-- Edit User Modal -->
<Modal bind:open={showEditModal} title="Edit User" size="sm">
	{#if editingUser}
		<div class="space-y-4">
			<div>
				<Label class="mb-1">Display Name</Label>
				<Input value={editingUser.displayName || ''} disabled />
				<p class="text-sm text-gray-500 dark:text-gray-400 mt-1">Managed through AbstractID</p>
			</div>
			<div>
				<Label class="mb-1">Email</Label>
				<Input value={editingUser.email || ''} disabled />
				<p class="text-sm text-gray-500 dark:text-gray-400 mt-1">Managed through AbstractID</p>
			</div>
			<div>
				<Toggle bind:checked={formIsAdmin}>Administrator</Toggle>
			</div>
		</div>
	{/if}
	<svelte:fragment slot="footer">
		<div class="flex gap-2">
			<Button color="primary" on:click={handleEdit} disabled={isSaving}>
				{#if isSaving}
					<Spinner size="sm" class="mr-2" />
				{/if}
				Save
			</Button>
			<Button color="alternative" on:click={() => (showEditModal = false)}>Cancel</Button>
		</div>
	</svelte:fragment>
</Modal>
